<template>
    <div
        v-bind:class="expandClasses"
        class="d-block">
        <div
            v-if="visible"
            v-bind:class="expandClasses">
            <slot />
        </div>
    </div>
</template>
<script>
    import { BP_ORDERED } from "Responsive/breakpoint";
    import { responsiveManager } from "Responsive/manager";
    import { Emitter } from "Util/emitter";

    // Keep track of whether viewport 'is at least' various breakpoints.
    // This allows injecting components to use the "isAtLeast" utility
    // function and have it properly scope to the nearest viewport if
    // present.
    const bpData = {};
    BP_ORDERED.forEach((bp) => {
        bpData[bp.name] = false;
    });

    export default {
        provide() {
            // Components that want their responsive behavior to be properly
            // determined by the viewport component must inject "viewport" and
            // define a responsive config that can be interpreted in the context
            // of either a viewport or the global responsiveManager. Then the
            // component's individual responsive classes can be defined as computeds
            // using the global util function responsiveManager.getResponsiveClass
            // and the relevant section of the responsive config. getResponsiveClass
            // will automatically use the most recent injected viewport, if defined,
            // or otherwise the global responsiveManager as the responsive context.
            return { viewport: this };
        },
        inject: {
            parentViewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            visible: { type: Boolean, required: false, default: true },
            expandToParentSize: { type: Boolean, required: false, default: false }
        },
        data() {
            return {
                emitter: new Emitter(),
                width: 0,
                ...bpData
            };
        },
        computed: {
            expandClasses() {
                return {
                    "w-100": this.expandToParentSize,
                    "h-100": this.expandToParentSize
                };
            },
            bp() {
                // return the name of the largest breakpoint than which the viewport is larger
                for (let i = BP_ORDERED.length - 1; i >= 0; i--) {
                    if (this.width >= BP_ORDERED[i].minWidth) {
                        return BP_ORDERED[i].name;
                    }
                }

                // Should never be called since smallest breakpoint
                // should be defined with a min-width of 0px
                return BP_ORDERED[0].name;
            }
        },
        watch: {
            visible() {
                this._updateWidth();
            },
            bp() {
                this._updateBpsSatisfied();
            }
        },
        created() {
            // If this viewport has an ancestor that is also a viewport, listen for updated events
            // so that we can propagate the update to this viewport.
            if (this.parentViewport) {
                this.parentViewport.addUpdatedListener(this.update);
            }
            responsiveManager.addResizeListener(this._updateWidth);
        },
        mounted() {
            this.update();
        },
        methods: {
            addUpdatedListener(callback) {
                this.emitter.on("updated", callback);
            },
            removeUpdatedListener(callback) {
                this.emitter.off("updated", callback);
            },
            async update() {
                await this._updateWidth();
                this.emitter.emit("updated");
            },
            async _updateWidth() {
                await this.$nextTick();
                this.width = this.$el.offsetWidth;
            },
            // Update individual breakpoint satisfied data properties based
            // on the value of the viewport's breakpoint. All breakpoints smaller
            // than and equal to the breakpoint will be considered satisfied.
            _updateBpsSatisfied() {
                let bpSatisfied = true;

                // Go through the data properties for the various breakpoints
                // starting from smallest, and mark them as satisfied. Once the
                // current value of the breakpoint is reached, every subsequent
                // larger breakpoint data property will be marked as not satisfied.
                for (let i = 0; i < BP_ORDERED.length; i++) {
                    this[BP_ORDERED[i].name] = bpSatisfied;
                    if (this.bp === BP_ORDERED[i].name) {
                        bpSatisfied = false;
                    }
                }
            }
        }
    };
</script>
